import { Button as CustomButton, Icon, HStack, VStack } from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { FinanceEnumsFileSharingTypeEnum } from '@genial/services/dist/models';
import { RouteProp, useNavigation, useRoute } from '@react-navigation/native';
import { Box, Button, Text } from 'native-base';
import React from 'react';
import { useRecoilValue } from 'recoil';

import {
  SignedInNavigationScreenNavigationProp,
  CustomerDetailStackParamList,
} from '../../../../../../../routes/types';
import { useCustomerActions } from '../../../../../../../store/customer/actions';
import { isExportingPdfForCustomers } from '../../../../../../../store/customer/atom';
import { useReportsActions } from '../../../../../../../store/customerDetail/reports/actions';
import {
  reportComplementaryTitleOptionsState,
  reportFilterParamsState,
  reportPeriodYearOptionState,
  reportTypeOptionState,
  reportYearOptionState,
} from '../../../../../../../store/customerDetail/reports/atom';
import {
  currentReportYearOption,
  initialReportMarketOption,
  initialReportMonthOption,
  initialReportPeriodDates,
  reportsType,
} from '../../../../../../../store/customerDetail/reports/constants';
import { reportPeriodIsDailySelector } from '../../../../../../../store/customerDetail/reports/selectors';
import { ExportPdfDropdown } from '../../../../shared/ExportPdfDropdown';
import { SelectType } from '../../SelectType';

import { HeaderResponsiveProps } from '.';

export const HeaderMobile: React.FC<HeaderResponsiveProps> = ({
  incomeTaxBaseYearReports,
  incomeTaxTypeReports,
}) => {
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const { params: routeParams } =
    useRoute<RouteProp<CustomerDetailStackParamList, 'CustomerDetail'>>();
  const accountNumber = routeParams?.accountNumber;
  const cpfCnpj = routeParams?.cpfCnpj;

  const reportTypeOption = useRecoilValue(reportTypeOptionState);
  const reportPeriodIsDaily = useRecoilValue(reportPeriodIsDailySelector);
  const brokerageInvoiceParams = useRecoilValue(reportFilterParamsState);
  const reportYearOptionStateValue = useRecoilValue(reportYearOptionState);
  const reportPeriodYearOption = useRecoilValue(reportPeriodYearOptionState);
  const reportComplementaryTitleOption = useRecoilValue(reportComplementaryTitleOptionsState);

  const { downloadIncomeStatementFile } = useCustomerActions();
  const { updateReportTypeOption, handleDownloadIncomeTaxComplementaryReport } =
    useReportsActions();

  const isExportingPdf = useRecoilValue(isExportingPdfForCustomers);
  const periodIsLoading = incomeTaxBaseYearReports.loading;
  const titlesIsLoading = incomeTaxTypeReports.loading;
  const buttonIsDisabled = periodIsLoading || titlesIsLoading;
  const buttonAnnualIncomeReportDownloadIsDisabled =
    periodIsLoading || titlesIsLoading || reportYearOptionStateValue === '';
  const reportsComplementaryTitlesOptionItems = incomeTaxTypeReports?.data || [];
  const buttonComplementaryReportDownloadIsDisabled =
    periodIsLoading ||
    titlesIsLoading ||
    reportPeriodYearOption === '' ||
    reportComplementaryTitleOption === '';
  const reportComplementaryItem = reportsComplementaryTitlesOptionItems?.find(
    (item) => item.value === reportComplementaryTitleOption
  );
  const fileTitle = reportComplementaryItem?.label || 'File';

  const handleDownloadAnnualIncome = (sharingType?: FinanceEnumsFileSharingTypeEnum) => {
    downloadIncomeStatementFile(cpfCnpj, Number(reportYearOption), sharingType);
  };

  const handleDownloadComplementaryReport = (sharingType?: FinanceEnumsFileSharingTypeEnum) => {
    handleDownloadIncomeTaxComplementaryReport({
      accountNumber,
      fileTitle,
      reportType: reportComplementaryTitleOption,
      yearReference: reportPeriodYearOption,
      sharingType,
    });
  };

  const params = {
    cpfCnpj,
    accountNumber,
    incomeTaxTypeReports,
    incomeTaxBaseYearReports,
  };

  const reportTypeOptionSelectedLabelIndex = reportsType.items?.findIndex(
    (item) => item.value === (reportTypeOption as unknown as string)
  );

  const selectedValue = reportsType.items[reportTypeOptionSelectedLabelIndex]?.label || 'Selecione';

  if (reportTypeOption === 'margin') {
    return (
      <HStack alignItems={'center'} justifyContent={'space-between'} p={Spacing['inline.xxxs']}>
        <SelectType
          isDisabled={isExportingPdf}
          title={reportsType.title.modal}
          onValueChange={(value) => updateReportTypeOption(value)}
          options={reportsType.items}
          selectedValue={selectedValue}
        />
        <CustomButton isDisabled>Buscar</CustomButton>
      </HStack>
    );
  }

  if (reportTypeOption === 'annual-income-report') {
    return (
      <VStack p={Spacing['inline.xxxs']}>
        <SelectType
          isDisabled={isExportingPdf}
          title={reportsType.title.modal}
          onValueChange={(value) => updateReportTypeOption(value)}
          options={reportsType.items}
          selectedValue={selectedValue}
        />
        <HStack alignItems="center" justifyContent="space-between" mt={Spacing['stack.xxs']}>
          <Button
            variant="secondary"
            isDisabled={buttonIsDisabled || isExportingPdf}
            onPress={() => navigate('ReportsFilter', params)}
            maxW={'90px'}>
            <HStack alignItems="center" justifyContent="center" space={Spacing['inset.quarck']}>
              <Text variant="semibold" fontSize={Typography['fontsize.md']}>
                Filtro
              </Text>
              <Icon name="Funnel" color={Color['neutral.darkest']} size="xxxxs" />
            </HStack>
          </Button>
          <HStack alignItems="center">
            <ExportPdfDropdown
              isExporting={isExportingPdf}
              isDisabled={buttonAnnualIncomeReportDownloadIsDisabled}
              onDownload={handleDownloadAnnualIncome}
              onSendToClient={() => handleDownloadAnnualIncome('Email')}
            />
          </HStack>
        </HStack>
      </VStack>
    );
  }

  if (reportTypeOption === 'complementary') {
    return (
      <VStack p={Spacing['inline.xxxs']}>
        <SelectType
          isDisabled={isExportingPdf}
          title={reportsType.title.modal}
          onValueChange={(value) => updateReportTypeOption(value)}
          options={reportsType.items}
          selectedValue={selectedValue}
        />
        <HStack alignItems="center" justifyContent="space-between" mt={Spacing['stack.xxs']}>
          <Button
            variant="secondary"
            isDisabled={buttonIsDisabled || isExportingPdf}
            onPress={() => navigate('ReportsFilter', params)}
            maxW={'90px'}>
            <HStack alignItems="center" justifyContent="center" space={Spacing['inset.quarck']}>
              <Text variant="semibold" fontSize={Typography['fontsize.md']}>
                Filtrar
              </Text>
              <Icon name="Funnel" color={Color['neutral.darkest']} size="xxxxs" />
            </HStack>
          </Button>
          <HStack alignItems="center">
            <ExportPdfDropdown
              isExporting={isExportingPdf}
              isDisabled={buttonComplementaryReportDownloadIsDisabled}
              onDownload={handleDownloadComplementaryReport}
              onSendToClient={() => handleDownloadComplementaryReport('Email')}
            />
          </HStack>
        </HStack>
      </VStack>
    );
  }

  const {
    reportMarketOption,
    reportMonthSelectedOptions,
    reportPeriodDates: { endDate, startDate },
    reportYearOption,
  } = brokerageInvoiceParams;

  const periodDateIsChanged =
    startDate !== initialReportPeriodDates.startDate || endDate !== initialReportPeriodDates.endDate
      ? 1
      : 0;

  const reportYearBrokerageOptionIsChanged = reportYearOption !== currentReportYearOption ? 1 : 0;
  const reportMarketOptionIsChanged = reportMarketOption !== initialReportMarketOption ? 1 : 0;
  const reportMonthSelectedOptionsIsChanged =
    reportMonthSelectedOptions !== initialReportMonthOption ? 1 : 0;

  const countBrokerageFilterDaily = periodDateIsChanged;
  const countBrokerageFilterMonth =
    reportYearBrokerageOptionIsChanged +
    reportMarketOptionIsChanged +
    reportMonthSelectedOptionsIsChanged;

  const countBrokerageFilterApplied = reportPeriodIsDaily
    ? countBrokerageFilterDaily
    : countBrokerageFilterMonth;

  return (
    <VStack>
      <HStack alignItems={'center'} justifyContent={'space-between'} p={Spacing['inline.xxxs']}>
        <SelectType
          title={reportsType.title.modal}
          onValueChange={(value) => updateReportTypeOption(value)}
          options={reportsType.items}
          selectedValue={selectedValue}
        />
        <Button variant="secondary" onPress={() => navigate('ReportsFilter', params)}>
          <HStack alignItems="center" justifyContent="center" space={Spacing['inset.quarck']}>
            <Text variant="semibold" fontSize={Typography['fontsize.md']}>
              Filtrar
            </Text>

            {countBrokerageFilterApplied > 0 ? (
              <Box
                rounded="full"
                w="21px"
                h="21px"
                alignItems="center"
                justifyContent="center"
                bg={'brand.tertiary.light'}>
                <Text
                  variant="semibold"
                  fontSize={Typography['fontsize.md']}
                  color={'neutral.white'}>
                  {countBrokerageFilterApplied}
                </Text>
              </Box>
            ) : (
              <Icon name="Funnel" color={Color['neutral.darkest']} size="xxxxs" />
            )}
          </HStack>
        </Button>
      </HStack>
    </VStack>
  );
};
